import { observable, action } from 'mobx';
import { request, formatApiDate } from 'utils/api';
import BaseStore from 'common/stores/BaseStore';

export default class TicketInventoryStore extends BaseStore {
  @observable inventoryForDate = new Map();
  @observable sessionsForDate = new Map();
  @observable calendarForRange = new Map();

  get(venueId, date) {
    return this.inventoryForDate.get(this.getKey(venueId, date));
  }

  getSessions(venueId, date) {
    return this.sessionsForDate.get(this.getKey(venueId, date)) || [];
  }

  getKey(venueId, date) {
    return `${venueId}-${formatApiDate(date)}`;
  }

  getInventoryItem({ venueId, reservationDate, ticketOptionId }) {
    const inventory = this.get(venueId, reservationDate);
    return inventory.find((item) => item.ticketOptionId === ticketOptionId);
  }

  getCalendarKey({ venueId, start, end, admission }) {
    return `${venueId}-${formatApiDate(start)}-${formatApiDate(end)}${
      admission ? `-${admission}` : ''
    }`;
  }

  getCalendar({ venueId, start, end, admission }) {
    return this.calendarForRange.get(
      this.getCalendarKey({ venueId, start, end, admission })
    );
  }

  @action
  invalidateCalendar(venueId) {
    const prefix = `${venueId}-`;
    Array.from(this.calendarForRange.keys()).forEach((key) => {
      if (key.startsWith(prefix)) this.calendarForRange.delete(key);
    });
  }

  @action
  fetchCalendar(
    { venueId, start, end, admission, forceRefresh } = {},
    statusKey = 'calendar'
  ) {
    const key = this.getCalendarKey({ venueId, start, end, admission });
    const cached = this.calendarForRange.get(key);
    if (cached && !forceRefresh) {
      return Promise.resolve(cached);
    }

    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/ticketinventory/calendar',
      params: {
        venueId,
        start: formatApiDate(start),
        end: formatApiDate(end),
        ...(admission && { admission }),
      },
    })
      .then(({ data }) => {
        const dates = data.dates || [];
        this.calendarForRange.set(key, dates);
        status.success();
        return dates;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  search(
    { venueId, date, promoCode, admission, forceRefresh, includeFee } = {},
    statusKey = 'search'
  ) {
    if (!forceRefresh) {
      console.warn(
        'Calling TicketInventoryStore.search with forceRefresh = false has no effect anymore'
      );
    }

    const status = this.createStatus(statusKey);
    return request({
      method: 'POST',
      path: '/1/ticketinventory/search',
      body: {
        venueId,
        promoCode,
        source: 'online',
        date: formatApiDate(date),
        ...(admission && { admission }),
        includeFee,
      },
    })
      .then(({ data }) => {
        const inventory = data.ticketInventory;

        const sessions = data.sessions;
        const calendarEntry = data.calendarEntry;

        this.inventoryForDate.set(this.getKey(venueId, date), inventory);

        this.sessionsForDate.set(this.getKey(venueId, date), sessions);

        if (calendarEntry) this.invalidateCalendar(venueId);

        status.success();
        return calendarEntry;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }
}
